import "@/App.css";
import React, { Suspense, lazy } from "react";
import { TravelObjectProvider } from "@/context/TravelObjectContext";
import { WorkspaceProvider } from "@/context/WorkspaceContext";
import { AppearanceProvider } from "@/context/AppearanceContext";
const Appearance = lazy(() => import("./pages/Appearance"));
const AdminSeasonalBranding = lazy(() => import("./pages/admin/AdminSeasonalBranding"));
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import GaTracker from "./components/GaTracker";
import RedirectResolver from "./components/RedirectResolver";
import { Toaster } from "sonner";
import { useAuth } from "./context/AuthContext";
const Landing = lazy(() => import("./pages/Landing"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
import AppLayout from "./components/AppLayout";
import BrandedLoader from "./components/BrandedLoader";
const Dashboard = lazy(() => import("./pages/Dashboard"));
const DailyReview = lazy(() => import("./pages/DailyReview"));
const CalendarPage = lazy(() => import("./pages/CalendarPage"));
const Vault = lazy(() => import("./pages/Vault"));
const VaultView = lazy(() => import("./pages/VaultView"));
const SecureShareDashboard = lazy(() => import("./pages/secureshare/SecureShareDashboard"));
const SearchResults = lazy(() => import("./pages/SearchResults"));
const SubscriptionJourney = lazy(() => import("./pages/SubscriptionJourney"));
const Household = lazy(() => import("./pages/Household"));
const Timeline = lazy(() => import("./pages/Timeline"));
const Notifications = lazy(() => import("./pages/Notifications"));
const Settings = lazy(() => import("./pages/Settings"));
const HealthScore = lazy(() => import("./pages/HealthScore"));
const ResponsibilityDetail = lazy(() => import("./pages/ResponsibilityDetail"));
const ReminderDetail = lazy(() => import("./pages/ReminderDetail"));
const ComingSoon = lazy(() => import("./pages/ComingSoon"));
const AcceptInvite = lazy(() => import("./pages/AcceptInvite"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Explore = lazy(() => import("./pages/Explore"));
const Responsibilities = lazy(() => import("./pages/Responsibilities"));
const PetsHub = lazy(() => import("./pages/PetsHub"));
const HomeLifeHub = lazy(() => import("./pages/HomeLifeHub"));
const BudgetHub = lazy(() => import("./pages/BudgetHub"));
const BudgetWorkspace = lazy(() => import("./pages/BudgetWorkspace"));
const WishlistShare = lazy(() => import("./pages/WishlistShare"));
const Billing = lazy(() => import("./pages/Billing"));
const GiftMemberships = lazy(() => import("./pages/GiftMemberships"));
const RedeemGift = lazy(() => import("./pages/RedeemGift"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const EmailChanged = lazy(() => import("./pages/EmailChanged"));
const RestoreAccount = lazy(() => import("./pages/RestoreAccount"));
const ManageCapacity = lazy(() => import("./pages/ManageCapacity"));
import ImpersonationBanner from "./components/ImpersonationBanner";
import CookieBanner from "./components/CookieBanner";
import { PremiumGate } from "./components/PremiumExperience";
const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminSeoGrowth = lazy(() => import("./pages/admin/AdminSeoGrowth"));
const AdminCommunityAnalytics = lazy(() => import("./pages/admin/AdminCommunityAnalytics"));
const ContentHub = lazy(() => import("./pages/admin/content/ContentHub"));
const ContentDashboard = lazy(() => import("./pages/admin/content/PlatformHealth"));
const SeoDiscovery = lazy(() => import("./pages/admin/content/SeoDiscovery"));
const PlanniTips = lazy(() => import("./pages/admin/content/PlanniTips"));
const ContentRegistry = lazy(() => import("./pages/admin/content/ContentRegistry"));
const TemplatesManager = lazy(() => import("./pages/admin/content/TemplatesManager"));
const DeveloperManaged = lazy(() => import("./pages/admin/content/DeveloperManaged"));
const ContentPermissions = lazy(() => import("./pages/admin/content/ContentPermissions"));
const CampaignManager = lazy(() => import("./pages/admin/content/studio/CampaignManager"));
const ContentTaxonomy = lazy(() => import("./pages/admin/content/Taxonomy"));
const MediaLibrary = lazy(() => import("./pages/admin/content/MediaLibrary"));
const StudioList = lazy(() => import("./pages/admin/content/studio/StudioList"));
const StudioEditor = lazy(() => import("./pages/admin/content/studio/StudioEditor"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminUserDetail = lazy(() => import("./pages/admin/AdminUserDetail"));
const AdminSubscriptions = lazy(() => import("./pages/admin/AdminSubscriptions"));
const AdminTransitions = lazy(() => import("./pages/admin/AdminTransitions"));
const AdminResponsibilities = lazy(() => import("./pages/admin/AdminResponsibilities"));
const AdminReminders = lazy(() => import("./pages/admin/AdminReminders"));
const AdminVault = lazy(() => import("./pages/admin/AdminVault"));
const AdminActivity = lazy(() => import("./pages/admin/AdminActivity"));
const AdminErrors = lazy(() => import("./pages/admin/AdminErrors"));
const AdminManagement = lazy(() => import("./pages/admin/AdminManagement"));
const AdminFeatureFlags = lazy(() => import("./pages/admin/AdminFeatureFlags"));
const AdminBetaAccess = lazy(() => import("./pages/admin/AdminBetaAccess"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminComingSoon = lazy(() => import("./pages/admin/AdminComingSoon"));
const AdminSupport = lazy(() => import("./pages/admin/AdminSupport"));
const AdminFeedback = lazy(() => import("./pages/admin/AdminFeedback"));
const AdminContentReports = lazy(() => import("./pages/admin/AdminContentReports"));
const About = lazy(() => import("./pages/company/About"));
const Contact = lazy(() => import("./pages/company/Contact"));
const SupportHub = lazy(() => import("./pages/support/SupportHub"));
const SupportStatus = lazy(() => import("./pages/support/SupportStatus"));
const MySupportRequests = lazy(() => import("./pages/support/MySupportRequests"));
const FAQ = lazy(() => import("./pages/company/FAQ"));
const Privacy = lazy(() => import("./pages/company/Privacy"));
const MobileScan = lazy(() => import("./pages/MobileScan"));
const Retention = lazy(() => import("./pages/company/Retention"));
const ProcessorRegister = lazy(() => import("./pages/company/ProcessorRegister"));
const Cookies = lazy(() => import("./pages/company/Cookies"));
const Terms = lazy(() => import("./pages/company/Terms"));
const AcceptableUse = lazy(() => import("./pages/company/AcceptableUse"));
const Trust = lazy(() => import("./pages/company/Trust"));
const Security = lazy(() => import("./pages/company/Security"));
const Principles = lazy(() => import("./pages/company/Principles"));
const Refund = lazy(() => import("./pages/company/Refund"));
const Status = lazy(() => import("./pages/company/Status"));
const Changelog = lazy(() => import("./pages/company/Changelog"));
const HelpCentre = lazy(() => import("./pages/company/HelpCentre"));
const PolicyLibrary = lazy(() => import("./pages/company/PolicyLibrary"));
const SubscriptionInformation = lazy(() => import("./pages/company/SubscriptionInformation"));
const LawEnforcement = lazy(() => import("./pages/company/LawEnforcement"));
const InformationSecurity = lazy(() => import("./pages/company/InformationSecurity"));
const VulnerabilityDisclosure = lazy(() => import("./pages/company/VulnerabilityDisclosure"));
const DataRights = lazy(() => import("./pages/company/DataRights"));
const DataBreachResponse = lazy(() => import("./pages/company/DataBreachResponse"));
const RoadmapPage = lazy(() => import("./pages/company/Roadmap"));
const FeatureRequests = lazy(() => import("./pages/company/FeatureRequests"));
const CommunityDashboard = lazy(() => import("./pages/community/CommunityDashboard"));
const Academy = lazy(() => import("./pages/community/Academy"));
const LessonDetail = lazy(() => import("./pages/community/LessonDetail"));
const ResourceCentre = lazy(() => import("./pages/community/ResourceCentre"));
const ArticleDetail = lazy(() => import("./pages/community/ArticleDetail"));
const ChecklistDetail = lazy(() => import("./pages/community/ChecklistDetail"));
const CommunityAnnouncements = lazy(() => import("./pages/community/Announcements"));
const Feedback = lazy(() => import("./pages/community/Feedback"));
const AdminAnnouncements = lazy(() => import("./pages/admin/AdminAnnouncements"));
const AdminBanners = lazy(() => import("./pages/admin/AdminBanners"));
const AdminSiteBanners = lazy(() => import("./pages/admin/AdminSiteBanners"));
const AdminVehicleLookup = lazy(() => import("./pages/admin/AdminVehicleLookup"));
const AdminTravelIntegrations = lazy(() => import("./pages/admin/AdminTravelIntegrations"));
const AdminTravelIntelligence = lazy(() => import("./pages/admin/AdminTravelIntelligence"));
const AdminProducts = lazy(() => import("./pages/admin/AdminProducts"));
const MoreFromPlannio = lazy(() => import("./pages/MoreFromPlannio"));
const HubManager = lazy(() => import("./pages/HubManager"));
const Travel = lazy(() => import("./pages/Travel"));
const VehicleHealthHub = lazy(() => import("./pages/VehicleHealthHub"));
const VehicleDashboard = lazy(() => import("./pages/VehicleDashboard"));
const PropertyHub = lazy(() => import("./pages/PropertyHub"));
const PropertyDashboard = lazy(() => import("./pages/PropertyDashboard"));
const BabyHub = lazy(() => import("./pages/BabyHub"));
const BabyJourneyDashboard = lazy(() => import("./pages/BabyJourneyDashboard"));
const BabySection = lazy(() => import("./pages/baby/BabySection"));
const EstateHub = lazy(() => import("./pages/estate/EstateHub"));
const EstateSetup = lazy(() => import("./pages/estate/EstateSetup"));
const ConnectHub = lazy(() => import("./pages/connect/ConnectHub"));
const ConnectionProfile = lazy(() => import("./pages/connect/ConnectionProfile"));
const SharedObjectView = lazy(() => import("./pages/connect/SharedObjectView"));
const Shared = lazy(() => import("./pages/Shared"));
const ConnectInvite = lazy(() => import("./pages/connect/ConnectInvite"));
const EstateSection = lazy(() => import("./pages/estate/EstateSection"));
const EstateDocuments = lazy(() => import("./pages/estate/EstateDocuments"));
const EstateContacts = lazy(() => import("./pages/estate/EstateContacts"));
const EstateWishes = lazy(() => import("./pages/estate/EstateWishes"));
const EstateMessages = lazy(() => import("./pages/estate/EstateMessages"));
const EstatePackPreview = lazy(() => import("./pages/estate/EstatePackPreview"));
const EstateDigitalAssets = lazy(() => import("./pages/estate/EstateDigitalAssets"));
const EstateInventory = lazy(() => import("./pages/estate/EstateInventory"));
const EstateBeneficiaries = lazy(() => import("./pages/estate/EstateBeneficiaries"));
const EstateReviews = lazy(() => import("./pages/estate/EstateReviews"));
const BabyTimeline = lazy(() => import("./pages/baby/BabyTimeline"));
const BabyKeepsake = lazy(() => import("./pages/baby/BabyKeepsake"));
const BabyHealth = lazy(() => import("./pages/baby/BabyHealth"));
const BabyFinancial = lazy(() => import("./pages/baby/BabyFinancial"));
const BabyNames = lazy(() => import("./pages/baby/BabyNames"));
const BabyJourneyBook = lazy(() => import("./pages/baby/BabyJourneyBook"));
const BabyResponsibilities = lazy(() => import("./pages/baby/BabyResponsibilities"));
const BabyFamilyStory = lazy(() => import("./pages/baby/BabyFamilyStory"));
const BabySettings = lazy(() => import("./pages/baby/BabySettings"));
const ForYouHome = lazy(() => import("./pages/foryou/ForYouHome"));
const ForYouJournal = lazy(() => import("./pages/foryou/ForYouJournal"));
const ForYouLists = lazy(() => import("./pages/foryou/ForYouLists"));
const ForYouLetters = lazy(() => import("./pages/foryou/ForYouLetters"));
const ForYouMeditation = lazy(() => import("./pages/foryou/ForYouMeditation"));
const NameExplorer = lazy(() => import("./pages/public/NameExplorer"));
const NamePage = lazy(() => import("./pages/public/NamePage"));
const NameInsights = lazy(() => import("./pages/public/NameInsights"));
const RegionalExplorer = lazy(() => import("./pages/public/RegionalExplorer"));
const NameCharts = lazy(() => import("./pages/public/NameCharts"));
const NameLandingPage = lazy(() => import("./pages/public/NameLandingPage"));
const NameCollection = lazy(() => import("./pages/public/NameCollection"));
const SharePage = lazy(() => import("./pages/public/SharePage"));
const TripDetail = lazy(() => import("./pages/TripDetail"));
const SharedTravelPack = lazy(() => import("./pages/SharedTravelPack"));
const AdminFeatureInterest = lazy(() => import("./pages/admin/AdminFeatureInterest"));
const AdminModeration = lazy(() => import("./pages/admin/AdminModeration"));
const AdminFeatureDetail = lazy(() => import("./pages/admin/AdminFeatureDetail"));
const AdminLegalPages = lazy(() => import("./pages/admin/AdminLegalPages"));
const AdminGovernance = lazy(() => import("./pages/admin/AdminGovernance"));
const AdminCompanyInfo = lazy(() => import("./pages/admin/AdminCompanyInfo"));
const AdminEpc = lazy(() => import("./pages/admin/AdminEpc"));
const Referrals = lazy(() => import("./pages/Referrals"));
const ReferralLanding = lazy(() => import("./pages/ReferralLanding"));
const AdminReferrals = lazy(() => import("./pages/admin/AdminReferrals"));
const AdminGifts = lazy(() => import("./pages/admin/AdminGifts"));
const AdminDestinationGuides = lazy(() => import("./pages/admin/AdminDestinationGuides"));
const AdminMinors = lazy(() => import("./pages/admin/AdminMinors"));
const AdminStorageAnalytics = lazy(() => import("./pages/admin/AdminStorageAnalytics"));
const AdminConsents = lazy(() => import("./pages/admin/AdminConsents"));
const AdminStatus = lazy(() => import("./pages/admin/AdminStatus"));
const AdminChangelog = lazy(() => import("./pages/admin/AdminChangelog"));
const AdminContent = lazy(() => import("./pages/admin/AdminContent"));
const AdminRoadmap = lazy(() => import("./pages/admin/AdminRoadmap"));
const AdminDiscovery = lazy(() => import("./pages/admin/AdminDiscovery"));
const AdminOnboarding = lazy(() => import("./pages/admin/AdminOnboarding"));
const AdminOperations = lazy(() => import("./pages/admin/AdminOperations"));
const AdminProductConfig = lazy(() => import("./pages/admin/AdminProductConfig"));
const AdminStorageTiers = lazy(() => import("./pages/admin/AdminStorageTiers"));
const AdminConnect = lazy(() => import("./pages/admin/AdminConnect"));
const AdminAuditLog = lazy(() => import("./pages/admin/AdminAuditLog"));
const AdminNotificationDelivery = lazy(() => import("./pages/admin/AdminNotificationDelivery"));
const AdminSiteContent = lazy(() => import("./pages/admin/AdminSiteContent"));
const AdminNameMeanings = lazy(() => import("./pages/admin/AdminNameMeanings"));
const AdminHubEntitlements = lazy(() => import("./pages/admin/AdminHubEntitlements"));
const AdminPrivacy = lazy(() => import("./pages/admin/AdminPrivacy"));
const AdminPrivacyCase = lazy(() => import("./pages/admin/AdminPrivacyCase"));
const AdminTasks = lazy(() => import("./pages/admin/AdminTasks"));
const StaffHome = lazy(() => import("./pages/admin/StaffHome"));
const StaffQueue = lazy(() => import("./pages/admin/StaffQueue"));
const OpsCase = lazy(() => import("./pages/admin/OpsCase"));
const OpsWorkspace = lazy(() => import("./pages/admin/OpsWorkspace"));
const AdminLegalOps = lazy(() => import("./pages/admin/AdminLegalOps"));
const InboundTriage = lazy(() => import("./pages/admin/InboundTriage"));
const Customer360 = lazy(() => import("./pages/admin/Customer360"));
const CommandCentre = lazy(() => import("./pages/admin/CommandCentre"));
const StaffAccess = lazy(() => import("./pages/admin/StaffAccess"));
const OpsGovernance = lazy(() => import("./pages/admin/OpsGovernance"));
const PrivacyVerify = lazy(() => import("./pages/PrivacyVerify"));
const PrivacyPortal = lazy(() => import("./pages/PrivacyPortal"));
const PrivacyCentre = lazy(() => import("./pages/PrivacyCentre"));
const PrivacyStatus = lazy(() => import("./pages/PrivacyStatus"));
const AdminCommunications = lazy(() => import("./pages/admin/AdminCommunications"));
const AdminSms = lazy(() => import("./pages/admin/AdminSms"));
const AdminDocs = lazy(() => import("./pages/admin/AdminDocs"));
const AdminFeatureCatalogue = lazy(() => import("./pages/admin/AdminFeatureCatalogue"));
const AdminSystemHealth = lazy(() => import("./pages/admin/AdminSystemHealth"));
const AdminDocSync = lazy(() => import("./pages/admin/AdminDocSync"));
const AdminConfigRegistry = lazy(() => import("./pages/admin/AdminConfigRegistry"));
const AdminArchitecture = lazy(() => import("./pages/admin/AdminArchitecture"));
const AdminOpsCalendar = lazy(() => import("./pages/admin/AdminOpsCalendar"));
const AdminOpsTimeline = lazy(() => import("./pages/admin/AdminOpsTimeline"));
const AdminTesting = lazy(() => import("./pages/admin/AdminTesting"));
const AdminSupportOps = lazy(() => import("./pages/admin/AdminSupportOps"));
const AdminSupportInsights = lazy(() => import("./pages/admin/AdminSupportInsights"));
const AdminResilience = lazy(() => import("./pages/admin/AdminResilience"));
const AdminAssistant = lazy(() => import("./pages/admin/AdminAssistant"));
const GuideDetail = lazy(() => import("./pages/community/GuideDetail"));

const Loader = () => <BrandedLoader fullscreen={true} />;

function Protected({ user, loading, children }) {
  if (loading || user === null) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ user, loading, children }) {
  if (loading || user === null) return <Loader />;
  if (user) {
    const next = new URLSearchParams(window.location.search).get("next");
    const safe = next && next.startsWith("/") && !next.startsWith("//") ? next : "/app/dashboard";
    return <Navigate to={safe} replace />;
  }
  return children;
}

function AdminProtected({ user, loading, children }) {
  if (loading || user === null) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <Navigate to="/app/dashboard" replace />;
  return children;
}

function App() {
  const { user, loading } = useAuth();
  return (
    <>
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <GaTracker />
        <AppearanceProvider>
        <ImpersonationBanner />
        <CookieBanner />
        <TravelObjectProvider>
        <Suspense fallback={<Loader />}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/r/:code" element={<ReferralLanding />} />
          <Route path="/login" element={<PublicOnly user={user} loading={loading}><Login /></PublicOnly>} />
          <Route path="/register" element={<PublicOnly user={user} loading={loading}><Register /></PublicOnly>} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/auth/email-changed" element={<EmailChanged />} />
          <Route path="/restore-account" element={<RestoreAccount />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/travel-pack/:token" element={<SharedTravelPack />} />
          <Route path="/s/:token" element={<SharePage />} />
          <Route path="/wishlist/:token" element={<WishlistShare />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/explore" element={<Explore />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/support" element={<SupportHub />} />
          <Route path="/support/status" element={<SupportStatus />} />
          <Route path="/support/status/:ref" element={<SupportStatus />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/scan/:token" element={<MobileScan />} />
          <Route path="/retention" element={<Retention />} />
          <Route path="/processor-register" element={<ProcessorRegister />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/privacy/verify" element={<PrivacyVerify />} />
          <Route path="/privacy/portal" element={<PrivacyPortal />} />
          <Route path="/privacy/requests" element={<PrivacyCentre />} />
          <Route path="/privacy/status" element={<PrivacyStatus />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/acceptable-use" element={<AcceptableUse />} />
          <Route path="/trust" element={<Trust />} />
          <Route path="/security" element={<Security />} />
          <Route path="/security/information-security-policy" element={<InformationSecurity />} />
          <Route path="/security/vulnerability-disclosure" element={<VulnerabilityDisclosure />} />
          <Route path="/data-rights" element={<DataRights />} />
          <Route path="/data-breach-response" element={<DataBreachResponse />} />
          <Route path="/principles" element={<Principles />} />
          <Route path="/refund" element={<Refund />} />
          <Route path="/status" element={<Status />} />
          <Route path="/changelog" element={<Changelog />} />
          <Route path="/names" element={<NameExplorer />} />
          <Route path="/names/charts" element={<NameCharts />} />
          <Route path="/names/insights" element={<NameInsights />} />
          <Route path="/names/insights/:year" element={<NameInsights />} />
          <Route path="/names/charts/:slug" element={<NameLandingPage />} />
          <Route path="/names/regional" element={<RegionalExplorer />} />
          <Route path="/names/boys" element={<NameCollection preset="boys" />} />
          <Route path="/names/girls" element={<NameCollection preset="girls" />} />
          <Route path="/names/popular" element={<NameCollection preset="popular" />} />
          <Route path="/names/rising" element={<NameCollection preset="rising" />} />
          <Route path="/names/falling" element={<NameCollection preset="falling" />} />
          <Route path="/names/:letter" element={<NameCollection preset="letter" />} />
          <Route path="/names/:sex/:name" element={<NamePage />} />
          <Route path="/help" element={<HelpCentre />} />
          <Route path="/help/:slug" element={<ArticleDetail />} />
          <Route path="/policies" element={<PolicyLibrary />} />
          <Route path="/subscription-information" element={<SubscriptionInformation />} />
          <Route path="/law-enforcement" element={<LawEnforcement />} />
          <Route path="/resources" element={<ResourceCentre />} />
          <Route path="/community/resources" element={<Navigate to="/resources" replace />} />
          <Route path="/roadmap" element={<RoadmapPage />} />
          <Route path="/feature-requests" element={<FeatureRequests />} />
          <Route path="/community" element={<CommunityDashboard />} />
          <Route path="/community/academy" element={<Academy />} />
          <Route path="/community/academy/:slug" element={<LessonDetail />} />
          <Route path="/community/articles/:slug" element={<ArticleDetail />} />
          <Route path="/community/checklists/:slug" element={<ChecklistDetail />} />
          <Route path="/community/guides/:slug" element={<GuideDetail />} />
          <Route path="/community/announcements" element={<CommunityAnnouncements />} />
          <Route path="/community/feedback" element={<Feedback />} />
          <Route path="/redeem-gift" element={<RedeemGift />} />
          <Route path="/connect/invite/:token" element={<ConnectInvite />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/app" element={<Protected user={user} loading={loading}><WorkspaceProvider><AppLayout /></WorkspaceProvider></Protected>}>
            <Route index element={<Navigate to="/app/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="daily-review" element={<DailyReview />} />
            <Route path="responsibilities" element={<Responsibilities />} />
            <Route path="pet" element={<PetsHub />} />
            <Route path="home-life" element={<HomeLifeHub />} />
            <Route path="budget" element={<BudgetHub />} />
            <Route path="budget/:id" element={<BudgetWorkspace />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="vault" element={<Vault />} />
            <Route path="vault/:vaultKey" element={<VaultView />} />
            <Route path="secure-share" element={<SecureShareDashboard />} />
            <Route path="search" element={<SearchResults />} />
            <Route path="household" element={<Household />} />
            <Route path="timeline" element={<Timeline />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="health" element={<HealthScore />} />
            <Route path="settings" element={<Settings />} />
            <Route path="support" element={<MySupportRequests />} />
            <Route path="support/:ref" element={<MySupportRequests />} />
            <Route path="appearance" element={<Appearance />} />
            <Route path="referrals" element={<Referrals />} />
            <Route path="billing" element={<Billing />} />
            <Route path="gifts" element={<GiftMemberships />} />
            <Route path="capacity" element={<ManageCapacity />} />
            <Route path="subscription-journey" element={<SubscriptionJourney />} />
            <Route path="travel" element={<Travel />} />
            <Route path="vehicle-health" element={<VehicleHealthHub />} />
            <Route path="vehicle-health/:id" element={<VehicleDashboard />} />
            <Route path="property" element={<PropertyHub />} />
            <Route path="property/:id" element={<PropertyDashboard />} />
            <Route path="baby" element={<BabyHub />} />
            <Route path="baby/:journeyId" element={<BabyJourneyDashboard />} />
            <Route path="baby/:journeyId/timeline" element={<BabyTimeline />} />
            <Route path="baby/:journeyId/memories" element={<BabyTimeline initialFilter="memories" />} />
            <Route path="baby/:journeyId/keepsake" element={<PremiumGate capability="baby.keepsakes"><BabyKeepsake /></PremiumGate>} />
            <Route path="baby/:journeyId/health" element={<PremiumGate capability="baby.health"><BabyHealth /></PremiumGate>} />
            <Route path="baby/:journeyId/financial" element={<PremiumGate capability="baby.finance"><BabyFinancial /></PremiumGate>} />
            <Route path="baby/:journeyId/names" element={<BabyNames />} />
            <Route path="baby/:journeyId/journey-book" element={<PremiumGate capability="baby.keepsakes"><BabyJourneyBook /></PremiumGate>} />
            <Route path="baby/:journeyId/responsibilities" element={<BabyResponsibilities />} />
            <Route path="baby/:journeyId/family-story" element={<BabyFamilyStory />} />
            <Route path="baby/:journeyId/settings" element={<BabySettings />} />
            <Route path="for-you" element={<ForYouHome />} />
            <Route path="for-you/journal" element={<ForYouJournal />} />
            <Route path="for-you/lists" element={<ForYouLists />} />
            <Route path="for-you/letters" element={<ForYouLetters />} />
            <Route path="for-you/meditation" element={<ForYouMeditation />} />
            <Route path="baby/:journeyId/:section" element={<BabySection />} />
            <Route path="estate" element={<EstateHub />} />
            <Route path="estate/setup" element={<EstateSetup />} />
            <Route path="estate/documents" element={<EstateDocuments />} />
            <Route path="estate/contacts" element={<EstateContacts />} />
            <Route path="estate/wishes" element={<EstateWishes />} />
            <Route path="estate/messages" element={<EstateMessages />} />
            <Route path="estate/digital-assets" element={<EstateDigitalAssets />} />
            <Route path="estate/inventory" element={<EstateInventory />} />
            <Route path="estate/beneficiaries" element={<EstateBeneficiaries />} />
            <Route path="estate/reviews" element={<EstateReviews />} />
            <Route path="estate/pack" element={<EstatePackPreview />} />
            <Route path="estate/:section" element={<EstateSection />} />
            <Route path="connect" element={<ConnectHub />} />
            <Route path="connect/connection/:id" element={<ConnectionProfile />} />
            <Route path="shared" element={<Shared />} />
            <Route path="shared/:sharedObjectId" element={<SharedObjectView />} />
            <Route path="travel/:id" element={<TripDetail />} />
            <Route path="more" element={<MoreFromPlannio />} />
            <Route path="hubs" element={<HubManager />} />
            <Route path="responsibility/:id" element={<ResponsibilityDetail />} />
            <Route path="reminder/:id" element={<ReminderDetail />} />
            <Route path="coming-soon/:slug" element={<ComingSoon />} />
          </Route>
          <Route path="/admin" element={<AdminProtected user={user} loading={loading}><AdminLayout /></AdminProtected>}>
            <Route index element={<Navigate to="/admin/home" replace />} />
            <Route path="home" element={<StaffHome />} />
            <Route path="queue" element={<StaffQueue />} />
            <Route path="ops/cases/:caseId" element={<OpsCase />} />
            <Route path="ops/legal" element={<AdminLegalOps />} />
            <Route path="ops/legal/:caseId" element={<AdminLegalOps />} />
            <Route path="ops/inbound-triage" element={<InboundTriage />} />
            <Route path="ops/:workspace" element={<OpsWorkspace />} />
            <Route path="customers/:userId" element={<Customer360 />} />
            <Route path="command-centre" element={<CommandCentre />} />
            <Route path="staff" element={<StaffAccess />} />
            <Route path="governance" element={<OpsGovernance />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="seo-growth" element={<AdminSeoGrowth />} />
            <Route path="community-analytics" element={<AdminCommunityAnalytics />} />
            <Route path="content-hub" element={<ContentHub />}>
              <Route index element={<ContentDashboard />} />
              <Route path="registry" element={<ContentRegistry />} />
              <Route path="templates" element={<TemplatesManager />} />
              <Route path="developer-managed" element={<DeveloperManaged />} />
              <Route path="permissions" element={<ContentPermissions />} />
              <Route path="taxonomy" element={<ContentTaxonomy />} />
              <Route path="media" element={<MediaLibrary />} />
              <Route path="seo" element={<SeoDiscovery />} />
              <Route path="planni-tips" element={<PlanniTips />} />
              <Route path="campaigns" element={<CampaignManager />} />
              <Route path="studio/:type" element={<StudioList />} />
              <Route path="studio/:type/:id" element={<StudioEditor />} />
            </Route>
            <Route path="analytics" element={<AdminComingSoon slug="analytics" title="Analytics" subtitle="Deeper product & engagement insights" blurb="Cohort retention, feature adoption, funnels and engagement trends are on the way." />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="users/:id" element={<AdminUserDetail />} />
            <Route path="subscriptions" element={<AdminSubscriptions />} />
            <Route path="transitions" element={<AdminTransitions />} />
            <Route path="features" element={<AdminFeatureInterest />} />
            <Route path="features/:slug" element={<AdminFeatureDetail />} />
            <Route path="responsibilities" element={<AdminResponsibilities />} />
            <Route path="reminders" element={<AdminReminders />} />
            <Route path="vault" element={<AdminVault />} />
            <Route path="support" element={<AdminSupport />} />
            <Route path="seasonal-branding" element={<AdminSeasonalBranding />} />
            <Route path="moderation" element={<AdminModeration />} />
            <Route path="feedback" element={<AdminFeedback />} />
            <Route path="content-reports" element={<AdminContentReports />} />
            <Route path="announcements" element={<Navigate to="/admin/content-hub/campaigns" replace />} />
            <Route path="community-posts" element={<Navigate to="/admin/content-hub/studio/announcement" replace />} />
            <Route path="banners" element={<AdminSiteBanners />} />
            <Route path="legacy/banners" element={<AdminBanners />} />
            <Route path="legacy/community-posts" element={<AdminAnnouncements />} />
            <Route path="integrations/vehicle-lookup" element={<AdminVehicleLookup />} />
            <Route path="travel/integrations" element={<AdminTravelIntegrations />} />
            <Route path="travel/intelligence" element={<AdminTravelIntelligence />} />
            <Route path="products" element={<AdminProducts />} />
            <Route path="activity" element={<AdminActivity />} />
            <Route path="errors" element={<AdminErrors />} />
            <Route path="management" element={<AdminManagement />} />
            <Route path="feature-flags" element={<AdminFeatureFlags />} />
            <Route path="beta-access" element={<AdminBetaAccess />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="legal-pages" element={<AdminLegalPages />} />
            <Route path="governance" element={<AdminGovernance />} />
            <Route path="company-info" element={<AdminCompanyInfo />} />
            <Route path="epc" element={<AdminEpc />} />
            <Route path="referrals" element={<AdminReferrals />} />
            <Route path="gifts" element={<AdminGifts />} />
            <Route path="destination-guides" element={<AdminDestinationGuides />} />
            <Route path="minors" element={<AdminMinors />} />
            <Route path="storage-analytics" element={<AdminStorageAnalytics />} />
            <Route path="consents" element={<AdminConsents />} />
            <Route path="status" element={<AdminStatus />} />
            <Route path="changelog" element={<AdminChangelog />} />
            <Route path="content" element={<AdminContent />} />
            <Route path="roadmap" element={<AdminRoadmap />} />
            <Route path="discovery" element={<AdminDiscovery />} />
            <Route path="onboarding" element={<AdminOnboarding />} />
            <Route path="operations" element={<AdminOperations />} />
            <Route path="product-config" element={<AdminProductConfig />} />
            <Route path="storage-tiers" element={<AdminStorageTiers />} />
            <Route path="connect" element={<AdminConnect />} />
            <Route path="audit-log" element={<AdminAuditLog />} />
            <Route path="notification-delivery" element={<AdminNotificationDelivery />} />
            <Route path="site-content" element={<AdminSiteContent />} />
            <Route path="name-meanings" element={<AdminNameMeanings />} />
            <Route path="hub-entitlements" element={<AdminHubEntitlements />} />
            <Route path="privacy" element={<AdminPrivacy />} />
            <Route path="privacy/cases/:caseId" element={<AdminPrivacyCase />} />
            <Route path="tasks" element={<AdminTasks />} />
            <Route path="communications" element={<AdminCommunications />} />
            <Route path="sms" element={<AdminSms />} />
            <Route path="docs" element={<AdminDocs />} />
            <Route path="feature-catalogue" element={<AdminFeatureCatalogue />} />
            <Route path="system-health" element={<AdminSystemHealth />} />
            <Route path="doc-sync" element={<AdminDocSync />} />
            <Route path="config-registry" element={<AdminConfigRegistry />} />
            <Route path="architecture" element={<AdminArchitecture />} />
            <Route path="ops-calendar" element={<AdminOpsCalendar />} />
            <Route path="ops-timeline" element={<AdminOpsTimeline />} />
            <Route path="testing" element={<AdminTesting />} />
            <Route path="support-ops" element={<AdminSupportOps />} />
            <Route path="support-insights" element={<AdminSupportInsights />} />
            <Route path="resilience" element={<AdminResilience />} />
            <Route path="assistant" element={<AdminAssistant />} />
          </Route>
          <Route path="*" element={<RedirectResolver />} />
        </Routes>
        </Suspense>
        </TravelObjectProvider>
        </AppearanceProvider>
      </BrowserRouter>
    </>
  );
}

export default App;
